import { EventEmitter } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { combineLatest, firstValueFrom, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';
import { AlertService, MessageSeverity } from 'src/app/services/alert.service';
import { InjectorInstance } from '../../../services/global/global.service';
import {
    AssessmentMode,
    EvaluationResult,
    EvaluationSeverityMode,
    ExecutionMode,
    IDisqualificationReason,
    IEvaluationRequest,
    IEvaluationResult,
    IInterviewEvent,
    IInterviewHandler,
    IInterviewSettings,
    InterviewAlternativeView, InterviewEventType,
    IVariable,
    IVariableEvaluationModel,
    Variable
} from '../models/interview-handler.models';
import { AbstractInterviewHandler } from './abstract-interview-handler.service';
import { VariableStringParserService } from './evaluation-formatters/variable-string-parser.service';
import { InterviewHandlerEndpointService } from './interview-handler-endpoint.service';
import { ControlMapping } from './variable-controls/control-mapping';
import { BaseVariableControl as BaseTemplateHandler } from './variable-controls/_base-control';
import { ICommandContext } from 'src/app/services/task/command/command.models';
import { Utilities } from '../../../services/utilities';
import { FeatureService } from '../../preview-feature/preview-feature.service';
import { MemberService } from '../../../services/member/member.service';
export class InterviewHandlerService extends AbstractInterviewHandler implements IInterviewHandler {

    controlMapping = ControlMapping;

    settings: IInterviewSettings;
    variables: IVariableEvaluationModel[] = [];
    evaluationResponseError: any;
    evaluationResponseNotifications: any;
    form = new UntypedFormGroup({});
    currentPageHasDisqualifications: boolean;
    disqualifications: IDisqualificationReason[] = [];
    currentPageIndex: number = 0;

    //#region Flags

    initialized: boolean;
    externalId: string;
    completed: boolean;
    evaluating: InterviewEventType;
    processingFinalization: boolean;
    showFinishDialog: boolean;
    handlingFinishing: boolean;
    showDisqualifications?: boolean;
    showDisqualificationsContinue: boolean;
    onValueChanged$$: Subscription;
    onButtonClick$$: Subscription;

    alternativeView: InterviewAlternativeView;

    public variableStringParserService: VariableStringParserService;
    private readonly endpointService: InterviewHandlerEndpointService;
    private readonly alertService: AlertService;
    public fmBuilder: UntypedFormBuilder;
    allowComplete: boolean;
    allowAdvance: boolean;
    featureService: FeatureService;
    memberService: MemberService;

    get allowPreviousNavigation() {
        const evaluationResult = this.events.evaluationResult$.value;
        return evaluationResult && evaluationResult.currentPage.index > 0;
    }

    get isPrimary() {
        return this.settings.mode === AssessmentMode.Primary;
    }

    //#endregion

    onValueChanged = new EventEmitter<BaseTemplateHandler>();
    onButtonClick = new EventEmitter<IVariable>();

    constructor(public commandContext?: ICommandContext) {
        super();
        this.variableStringParserService = InjectorInstance.get(VariableStringParserService);
        this.endpointService = InjectorInstance.get(InterviewHandlerEndpointService);
        this.alertService = InjectorInstance.get(AlertService);
        this.fmBuilder = InjectorInstance.get(UntypedFormBuilder);
        this.featureService = InjectorInstance.get(FeatureService);
        this.memberService = InjectorInstance.get(MemberService);
    }

    async initialize(settings: IInterviewSettings): Promise<any> {
        this.clear();

        this.settings = settings;
        console.log('init....................', this.settings);
        await this.events.initializeEvaluationSeverityMode();

        await this.featureService.initializeFeature("ENABLE-DEPENDENT-VARIABLES", { TenantId: this.memberService.tenantIdSync });

        if (this.settings.mode === AssessmentMode.Primary) {
            this.initializeContextEvents();
        }

        this.allowComplete = false;
        this.externalId = undefined;

        if (!this.settings.dataProvider || await this.settings.dataProvider.canInitializeInterview(this)) {

            await this.evaluateInternal(InterviewEventType.Forward);

            if (!this.initialized) {

                this.initialized = true;

                if (this.isPrimary) {
                    this.events.lifeCycleEvent$.next({ type: InterviewEventType.Initialized, handler: this } as IInterviewEvent);
                }
            }
        }
    }

    onButtonClick$ = new Subject<IVariable>();

    private initializeContextEvents() {

        this.onValueChanged$$ = this.onValueChanged.subscribe(async (templateHandler: BaseTemplateHandler) => {
            if ((templateHandler.variable.dependencies && templateHandler.variable.dependencies.length > 0) || templateHandler.variable.forcePostback) {
                await this.evaluateInternal(InterviewEventType.Postback);
            }

            this.events.onValueChanged$.next(templateHandler);
        });

        this.onButtonClick$$ = this.onButtonClick.subscribe(async (variable: IVariable) => {
            this.onButtonClick$.next(variable);
        });
    }

    public async evaluate(action: InterviewEventType, variables?: IVariableEvaluationModel[]) {
        await this.evaluateInternal(action, null, variables);
    }

    get isDependentVariablesEnabled() {
        return this.featureService.hasFeature('ENABLE-DEPENDENT-VARIABLES');
    }

    get hasFatalErrors() {
        return this.evaluationResponseNotifications &&
            this.evaluationResponseNotifications.some(x => x.severity.toLowerCase() === 'error')
    }

    //#region Evaluation
    protected async evaluateInternal(action: InterviewEventType, targetPage?: number, variables?: IVariableEvaluationModel[]) {

        if (variables) {
            this.mergeVariables(variables);
        }

        const evaluationRequest = await this.createEvaluationRequest(action, targetPage, variables);

        const lastEvaluationResult = this.events.evaluationResult$.value;

        evaluationRequest.request.pagingInfo.pageIndex =
            evaluationRequest.request.pagingInfo.pageIndex != null ? evaluationRequest.request.pagingInfo.pageIndex : (lastEvaluationResult && lastEvaluationResult.currentPage && lastEvaluationResult.currentPage.index) || 0;

        this.evaluationResponseError = null;

        this.evaluationResponseNotifications = null;
        try {
            this.setEvaluating(action);

            const evaluationResponse = await this.endpointService.callEvaluate(evaluationRequest).pipe(
                catchError(error => {
                    this.evaluationResponseError = error;
                    this.alertService.showStickyMessage(`Interview error`, `An error occurs when processing the interview`, MessageSeverity.error, error);
                    return of(null as IEvaluationResult);
                })
            )
                .toPromise();

            if (evaluationResponse && evaluationResponse?.interviewErrors) {
                this.evaluationResponseNotifications = evaluationResponse.interviewErrors.map(v => {
                    return {
                        summary: v.message,
                        detail: v.detail,
                        severity: v.severity?.toLowerCase(),
                        closeable: false,
                    }
                });
            }

            if (!this.hasFatalErrors) {
                await this.process(evaluationRequest, evaluationResponse);
            }

        } finally {
            this.setEvaluating();
        }

        if (this.isPrimary) {
            if (!evaluationRequest.request.initialize) {
                this.events.lifeCycleEvent$.next({ type: action, handler: this } as IInterviewEvent);
            }
        } else {
            this.events.lifeCycleEvent$.next({ type: InterviewEventType.Evaluation, handler: this } as IInterviewEvent);
        }
    }

    async getInterviewDetails(): Promise<IEvaluationResult> {

        const evaluationRequestPayload = await this.createEvaluationRequest(InterviewEventType.Forward);
        evaluationRequestPayload.request.executionMode = ExecutionMode.RequestStructure;
        evaluationRequestPayload.request.initialize = true;

        const evaluationResponse = await this.endpointService.callEvaluate(evaluationRequestPayload).pipe(
            catchError(error => {
                this.evaluationResponseError = error;
                return of(null);
            }),
        )
            .toPromise();

        return new EvaluationResult(evaluationResponse);
    }

    private async createEvaluationRequest(action: InterviewEventType, targetPage?: number, variables?: IVariableEvaluationModel[]) {
        this.variables = this.prepareVariablesToEvaluate(variables);

        const customContextData = await this.getInterviewContextData();

        return {
            packageName: this.settings.package,
            version: this.settings.version,
            request: {
                executionMode: this.settings.executionMode,
                mode: this.settings.mode,
                evaluationSeverityMode: this.events.getSeveritySync(),
                contextData: { ...customContextData, ExternalId: customContextData.ExternalId ?? this.externalId ?? null },
                variables: this.variables || [],
                initialize: !this.initialized,
                pagingInfo: {
                    pageIndex: targetPage != null ? targetPage : (this.events.evaluationResult$.value && this.events.evaluationResult$.value.currentPage.index),
                    pageSize: this.settings.pageSize || 10,
                    action
                },
            }
        } as IEvaluationRequest;
    }

    public setComplete() {

        this.completed = true;

        this.showDisqualificationsContinue = false;
        this.allowComplete = true;
        this.allowAdvance = false;

        if (this.settings.summaryComponentId) {
            this.showDisqualifications = false;

            this.events.evaluationResult$.value.currentPage.index = 99999
        }
        else {
            this.showDisqualifications = true;
        }
    }

    setEvaluating(evaluating?: InterviewEventType) {
        console.log(evaluating);

        this.evaluating = evaluating;
    }

    clearFormControls() {

        if (this.events.evaluationResult$.value) {

            this.events.evaluationResult$.value.currentPage.variables.forEach(v => {

                v.displayMetadata.formControl.dispose();

            });
        }
    }
    //#endregion


    categories(onClick?: (event: any) => Observable<void>) {
        return combineLatest([this.events.evaluationResult$, this.events.completionProcess$.asObservable()]).pipe(map(([result, _]) => {
            const mapping = result ? result.categories
                .map(categoryItem => {
                const category = {
                    label: categoryItem.displayName,
                    item: categoryItem,
                    styleClass: (categoryItem.hasValidationErrors ? 'step-icon step-error' : 'step-icon step-check step-success'),
                    command: onClick || (async (event: any) => {
                        await this.toPage(event.item.item.firstPageIndex);
                    })
                } as any;

                if (categoryItem.hasValidationErrors) {
                    category.styleClass = (category.styleClass || '') + ` step-error`;
                }
                return category;
            }) : [];


            if (result && result.interviews[0] && this.settings.summaryComponentId) {

                mapping.push({
                    label: 'Summary',
                    item: null,
                    styleClass: `step-icon step-info step-${result?.interviews[0].evaluationStatus.toLowerCase() ?? 'unknown'}`,

                    command: onClick || (async (event: any) => {
                        if (result.interviews[0].evaluationStatus.toUpperCase() !== 'UNKNOWN') {
                            this.setComplete()
                            this.events.lifeCycleEvent$.next({ type: InterviewEventType.Refresh, handler: this } as IInterviewEvent);
                        }
                    })
                });
            }
            else {
                mapping.push({
                    label: '',
                    item: null,
                    styleClass: `step-icon step-end step-${this.completed ? 'complete' : 'pending'}`
                });
            }

            return mapping;
        }));
    }

    prepareVariablesToEvaluate(variables?: IVariableEvaluationModel[]): IVariableEvaluationModel[] {

        if (this.events.evaluationResult$.value) {

            if (this.isPrimary) {

                const currentPageVariables = this.events.evaluationResult$.value.currentPage.variables
                    .filter(variable => !!variable.displayMetadata && variable.displayMetadata.template !== "script")
                    .filter(variable => !!variable.displayMetadata && variable.displayMetadata.templateData?.identifier !== "NotificationProvider")
                    .filter(variable => !!variable.displayMetadata && variable.displayMetadata.templateData?.identifier !== "ButtonProvider")
                    .map(variableItem =>
                        (
                            {
                                name: variableItem.name,
                                categoryName: variableItem.categoryName,
                                value: variableItem.displayMetadata.formControl.getValue(null),
                                metadata: variableItem.metadata || {}
                            } as IVariableEvaluationModel
                        )
                    );

                const buttonVariables = this.events.evaluationResult$.value.currentPage.variables
                    .filter(variable => !!variable.displayMetadata && variable.displayMetadata.templateData?.identifier == "ButtonProvider")
                    .filter(variable => !!variable.displayMetadata && variable.hasValue)
                    .map(variableItem =>
                        (
                            {
                                name: variableItem.name + "-Status",
                                categoryName: variableItem.categoryName,
                                value: "CLICKED",
                                metadata: variableItem.metadata || {}
                            } as IVariableEvaluationModel
                        )
                    );

                const currentContentVariables = this.events.evaluationResult$.value.variables
                    .filter(variableItem => !currentPageVariables.some(currentVariable => currentVariable.name === variableItem.name))
                    .map(variableItem =>
                    (
                        {
                            name: variableItem.name,
                            categoryName: variableItem.categoryName,
                            value: (variableItem.displayMetadata ? variableItem.displayMetadata.formControl.getValue(null) : variableItem.value),
                            metadata: variableItem.metadata || {}
                        } as IVariableEvaluationModel
                    )
                    );

                return [...currentContentVariables, ...currentPageVariables, ...buttonVariables];
            } else {
                const currentContentVariables = Utilities.cloneHard(this.events.evaluationResult$.value.variables) as IVariableEvaluationModel[];

                variables.forEach(updatedItem => {
                    const existingVariable = currentContentVariables.find(variableItem => variableItem.name === updatedItem.name);

                    if (!existingVariable) {
                        // Add new variable if it doesn't exist
                        currentContentVariables.push({
                            name: updatedItem.name,
                            value: updatedItem.value
                        });
                    }
                });
                return [...currentContentVariables];
            }
        }
    }

    finish() {
        if (!this.handlingFinishing) {
            this.finished();
        } else {
            this.events.lifeCycleEvent$.next({ type: InterviewEventType.Finish, handler: this } as IInterviewEvent);
        }
    }

    finished() {
        this.events.lifeCycleEvent$.next({ type: InterviewEventType.Finished, handler: this } as IInterviewEvent);
    }

    activeNextButton() {
        return this.allowAdvance
            &&
            this.form.valid;
    }

    activeForwardButton() {
        return this.allowAdvance
            &&
            this.form.valid;
    }

    activeFinishButton() {
        return this.allowComplete;
    }

    async getInterviewContextData(settings?: any) {
        return this.settings.contextDataCallback ? await this.settings.contextDataCallback(settings) : await this.settings.dataProvider?.generateContextData(this.settings.extraContextData);
    }

    async finalize(extraContextData?: any) {

        const evaluatingOriginal = this.evaluating;

        try {
            console.log("finalize");

            if (!evaluatingOriginal) {
                this.setEvaluating(InterviewEventType.Evaluation);
            }

            const finalizeRequestPayload = await this.buildFinalizeRequestPayload(extraContextData);

            return await firstValueFrom(this.endpointService.finalizeInterview(finalizeRequestPayload).pipe(first()));
        }
        finally {
            this.setEvaluating(evaluatingOriginal);
        }

    }

    async finalizev2(extraContextData?: any) {

        const evaluatingOriginal = this.evaluating;

        try {
            console.log("finalize V2");

            if (!evaluatingOriginal) {
                this.setEvaluating(InterviewEventType.Evaluation);
            }

            const finalizeRequestPayload = await this.buildFinalizeRequestPayload(extraContextData);

            return await firstValueFrom(this.endpointService.finalizeInterviewV2(finalizeRequestPayload).pipe(first()));
        }
        finally {

            this.setEvaluating(evaluatingOriginal);
        }

    }

    protected async buildFinalizeRequestPayload(extraContextData: any) {
        this.variables = this.prepareVariablesToEvaluate();
        const customContextData = await this.getInterviewContextData({ ...extraContextData, finalize: true });

        return {
            packageName: this.settings.package,
            version: this.settings.version,
            request: {
                executionMode: this.settings.executionMode,
                evaluationSeverityMode: this.events.getSeveritySync(),
                contextData: { ...customContextData, ...extraContextData, ExternalId: this.externalId ?? null },
                variables: this.variables || [],
                initialize: !this.initialized,
                pagingInfo: {
                    pageIndex: this.events.evaluationResult$.value && this.events.evaluationResult$.value.currentPage.index,
                    pageSize: this.settings.pageSize || 10,
                    action: InterviewEventType.Postback
                }
            }
        } as IEvaluationRequest;
    }

    clear() {

        console.log("Clear")
        this.initialized = false;
        this.externalId = undefined;
        this.completed = false;
        this.evaluating = undefined;
        this.processingFinalization = false;
        this.showErrorsAllowed = false;
        this.showFinishDialog = false;
        this.handlingFinishing = false;
        this.showDisqualifications = false;

        this.clearFormControls();
        this.events.clear();
        this.form.reset();

        try {
            this.onValueChanged$$.unsubscribe();
        } catch (error) {
            console.warn(`Error closing subscription for valueChanged(onValueChanged$$) in interviewHandler`);
        }

        try {
            this.onButtonClick$$.unsubscribe();
        } catch (error) {
            console.warn(`Error closing subscription for valueChanged(onButtonClick$$) in interviewHandler`);
        }
    }


    // #region Evaluation IPageResult
    private async process(request: IEvaluationRequest, response: IEvaluationResult) {

        if (!response) {
            throw new Error('Evaluation result is null');
        }

        this.externalId = response.externalId;
        this.settings.dataProvider?.processEvaluation(response);

        this.clearFormControls();

        const result = new EvaluationResult(response);
        result.currentPageVariables.forEach(variable => variable.format(this, this.variableStringParserService));

        this.form.markAllAsTouched();
        this.form.updateValueAndValidity();

        this.currentPageIndex = result.currentPage.index;

        if (this.isPrimary) {

            const samePage = this.events.evaluationResult$.value && result.currentPage.index === this.events.evaluationResult$.value.currentPage.index;
            const interview = result.interviews[0];

            this.showErrorsAllowed = result.currentPage.hasError
                && (samePage && [InterviewEventType.Previous, InterviewEventType.Next, InterviewEventType.Postback].some(item => item === request.request.pagingInfo.action))
                || request.request.pagingInfo.action === InterviewEventType.Forward;

            this.disqualifications = (interview?.qualificationResultItems || [])
                .map(a => a.disqualifications)
                .reduce((prev, current) => [...prev, ...current], [])
                .filter(item => item.code === 'DISQUALIFICATION');

            this.manageNavigation(request, result);
        }

        // Move full list of variables to the new/last result
        if (!request.request.initialize) {

            if (this.isDependentVariablesEnabled) {

                result.variables = this.mergeVariablesFromBackend(response.variables ?? [], this.variables ?? []);
            }
            else {

                result.variables = (this.variables || []).map(variable => {

                    return new Variable({ ...variable } as IVariable);
                });
            }
        }

        this.events.evaluationResult$.next(result);
    }

    private manageNavigation(request: IEvaluationRequest, result: IEvaluationResult) {
        const interviewsStatus = result.interviews.map(interviewResultItem => ({ hasEvaluationErrors: interviewResultItem.hasEvaluationErrors, evaluationStatus: interviewResultItem.evaluationStatus }))[0].evaluationStatus.toUpperCase();
        const currentPageHasValidationErrors = result.categories.find(category => result.currentPage.categoryName == category.name)?.hasValidationErrors ?? true;

        // Process the navigation rules in the specific order defined below
        if (!this.initialized) {
            // If the interview is not initialized and the evaluation status is Disqualified, we set the interview as complete
            this.completed = interviewsStatus != 'UNKNOWN' && !result.currentPage.forceStop;
        }
        else if (interviewsStatus == 'UNKNOWN') {
            // If the interview is initialized and the evaluation status is Unknown, the interview can be completed
            this.completed = false;
        }
        else if (result.currentPage.isLastPage && [InterviewEventType.Forward].some(action => action === request.request.pagingInfo.action)) {
            // Allow the interview to complete if the last action was Forward and the current page is the last page
            this.completed = !result.currentPage.forceStop;
        }
        else if (this.events.evaluationResult$.value.currentPage.isLastPage && [InterviewEventType.Next].some(action => action === request.request.pagingInfo.action)) {
            // Allow to complete the interview if the action was Next and the previous page was the last page
            this.completed = true;
        }
        else if ([InterviewEventType.Goto, InterviewEventType.Previous, InterviewEventType.Next, InterviewEventType.Forward, InterviewEventType.Postback].some(action => action === request.request.pagingInfo.action)) {
            // If the last action was Goto, Previous, Next or Forward, the interview cannot be completed. Note, Next and Forward have additional rules above
            this.completed = false;
        }
        else {
            // Default: Use the evaluation status to determine if the interview is complete
            this.completed = interviewsStatus != 'UNKNOWN';
        }

        if (this.completed) {

            this.showDisqualificationsContinue = false
            this.allowComplete = !currentPageHasValidationErrors;
            this.allowAdvance = false;

            if (this.settings.summaryComponentId) {
                this.showDisqualifications = false;

                result.currentPage.index = 99999;
            }
            else {
                this.showDisqualifications = interviewsStatus === 'DISQUALIFIED';
                //If interview has disqualifications, allow to complete
                this.allowComplete = this.showDisqualifications ? true : this.allowComplete;
            }
        }
        else {
            // If the interview is not complete, we need to check if there are disqualifications
            // const currentPageDisqualificationsByVariable = this.disqualifications.filter(disqualification => result.currentPage.variables.find(variable => variable.name === disqualification.sourceName) != null);
            // const currentPageDisqualificationsByCategory = this.disqualifications.filter(disqualification => result.currentPage.categoryName.toUpperCase() == disqualification.sourceName.toUpperCase());
            if (!this.settings.byPassToSummary) {
                this.showDisqualifications = this.settings.byPassToSummary;
                this.allowAdvance = !this.settings.byPassToSummary;
            }
            else {
                this.showDisqualifications = interviewsStatus.toUpperCase() === 'DISQUALIFIED';
                this.allowAdvance = interviewsStatus.toUpperCase() !== 'DISQUALIFIED';
            }
            console.log('YYYYYYYYYYYYYYYYYYYYYYYYYYYYYY', this.settings);
            if (this.settings.summaryComponentId) {
                console.log('SSSSSSSSSSSSSSSSSSSSSSSSSS', this.settings);

                //this allow user to jump to summary page and enable finish button
                this.showDisqualificationsContinue = true
                this.allowComplete = false;
            }
            else {
                //If current page has disqualifications, do not allow to advance
                //if the interview is not complete, does not have a summary page, and disqualified - show complete button
                this.showDisqualificationsContinue = false;
                this.allowComplete = !this.allowAdvance && interviewsStatus !== 'UNKNOWN';
                this.allowAdvance = this.allowAdvance && !this.allowComplete;
            }
        }
    }
    //#endregion

    async toggleSeverityMode() {
        let evaluationSeverity = this.events.getSeveritySync();
        evaluationSeverity = (evaluationSeverity === EvaluationSeverityMode.Lineant ? EvaluationSeverityMode.Strict : EvaluationSeverityMode.Lineant);
        this.events.setSeverity(evaluationSeverity);
        if (this.initialized) {
            await this.forward();
        }
    }


    private mergeVariables(updatedVariables: IVariableEvaluationModel[]) {

        const evaluationResult = this.events.evaluationResult$.value;
        evaluationResult.variables.forEach(variableItem => {
            const updatedVariable = updatedVariables.find(updatedItem => updatedItem.name === variableItem.name);
            if (updatedVariable) {
                variableItem.value = updatedVariable.value;
            }
        });
        this.events.evaluationResult$.next(evaluationResult);
    }

    private mergeVariablesFromBackend(updatedVariables: IVariable[], sourceVariables: IVariableEvaluationModel[]) {

        const result: IVariable[] = sourceVariables as IVariable[];
        if (sourceVariables) {
            updatedVariables.forEach(updatedVariableItem => {
                const repositoryVariable = sourceVariables.find(updatedItem => updatedItem.name === updatedVariableItem.name);
                if (repositoryVariable) {
                    repositoryVariable.value = updatedVariableItem.value;
                }
                else {
                    result.push({ name: updatedVariableItem.name, value: updatedVariableItem.value } as IVariable);
                }
            });
        }
        return result;
    }
}
